/**
 * PropreratiLocalityCombobox — autocomplete de localidad de Córdoba verificado
 * contra Properati.
 *
 * Regla innegociable: nunca muestra una localidad de otra provincia. El
 * backend ya filtra por Córdoba y verifica el slug contra el <title> real de
 * la SERP; acá sólo pintamos y notificamos la elección.
 *
 * Props:
 *   value           – objeto PropreratiRef seleccionado (o null)
 *   onSelect(ref)   – ref = {properati_id, nombre_canonico, slug_verificado,
 *                            provincia, tipo_geo, resultados_totales, verificable}
 *                     ref.verificable=false → viene de IDECOR (Properati caído),
 *                     el consumer decide si permite capturar.
 *   placeholder     – texto del input vacío
 *   testId          – data-testid del root (default "properati-loc-combobox")
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const DEBOUNCE_MS = 250;

export function PropreratiLocalityCombobox({ value, onSelect, placeholder = "Escribí una localidad de Córdoba…", testId = "properati-loc-combobox" }) {
  const [q, setQ] = useState(value?.nombre_canonico || "");
  const [items, setItems] = useState([]);
  const [fuente, setFuente] = useState(null);  // "properati" | "idecor" | null
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(0);
  const rootRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync input if `value` prop changes externally (edit dialog re-open).
  useEffect(() => {
    if (value?.nombre_canonico) setQ(value.nombre_canonico);
  }, [value?.nombre_canonico]);

  // Debounced fetch on q.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const query = q.trim();
    if (query.length < 2) {
      setItems([]); setFuente(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get(`/localidades/autocomplete`, { params: { q: query } });
        setItems(r.data?.items || []);
        setFuente(r.data?.fuente || null);
        setHoverIdx(0);
      } catch (e) {
        setItems([]); setFuente(null);
      } finally {
        setBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (item) => {
    setQ(item.nombre_canonico);
    setOpen(false);
    onSelect?.(item);
  };

  const onKeyDown = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHoverIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(items[hoverIdx]); }
    else if (e.key === "Escape") setOpen(false);
  };

  const showSinCobertura = q.trim().length >= 2 && !busy && items.length === 0;

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          data-testid={`${testId}-input`}
          type="text"
          value={q}
          placeholder={placeholder}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="w-full pl-9 pr-9 py-2 border border-slate-300 rounded-none text-sm focus:outline-none focus:border-slate-900 bg-white"
        />
        {busy && <Loader2 className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
      </div>

      {/* Dropdown */}
      {open && q.trim().length >= 2 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-slate-300 shadow-lg max-h-64 overflow-y-auto"
             data-testid={`${testId}-dropdown`}>
          {items.length > 0 && items.map((it, i) => (
            <button
              key={`${it.properati_id || 'idecor'}-${i}`}
              type="button"
              onClick={() => pick(it)}
              onMouseEnter={() => setHoverIdx(i)}
              data-testid={`${testId}-option-${i}`}
              className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-2 ${i === hoverIdx ? "bg-slate-100" : "hover:bg-slate-50"}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{it.nombre_canonico}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {it.subtitulo || "Córdoba"} · {it.tipo_geo}
                  {it.resultados_totales > 0 && <span className="text-slate-400"> · {it.resultados_totales} avisos</span>}
                </p>
              </div>
              {it.verificable ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" title="Verificado en Properati" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" title="Sólo IDECOR — Properati no respondió" />
              )}
            </button>
          ))}
          {showSinCobertura && (
            <div className="px-3 py-3 text-sm text-slate-500" data-testid={`${testId}-empty`}>
              Sin cobertura en Córdoba para &ldquo;<span className="font-medium">{q}</span>&rdquo;.<br/>
              <span className="text-[11px]">No aparece en Properati con provincia Córdoba y tampoco en el catálogo IDECOR local.</span>
            </div>
          )}
        </div>
      )}

      {/* Estado del valor seleccionado — dá contexto siempre visible */}
      {value?.slug_verificado ? (
        <p className="mt-1.5 text-[11px] text-emerald-700 flex items-center gap-1" data-testid={`${testId}-status-verified`}>
          <CheckCircle2 className="w-3 h-3" /> Verificado · <span className="font-mono">{value.slug_verificado}</span>{value.resultados_totales > 0 && <> · {value.resultados_totales} avisos</>}
        </p>
      ) : value?.nombre_canonico && !value?.verificable ? (
        <p className="mt-1.5 text-[11px] text-amber-700 flex items-center gap-1" data-testid={`${testId}-status-fallback`}>
          <AlertTriangle className="w-3 h-3" /> Sólo IDECOR — hasta que Properati responda, esta zona no se puede capturar.
        </p>
      ) : (
        <p className="mt-1.5 text-[11px] text-slate-400">
          {fuente === "idecor" && "Properati no respondió — mostramos catálogo IDECOR local."}
        </p>
      )}
    </div>
  );
}

export default PropreratiLocalityCombobox;
